export abstract class DomainError extends Error {
  abstract readonly tag: string;

  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    // Without this, every subclass instance logs as "Error" because Error captures name eagerly.
    this.name = this.constructor.name;
  }
}

export class ServerError extends DomainError {
  readonly tag = 'ServerError' as const;
}

export class ValidationError extends DomainError {
  readonly tag = 'ValidationError' as const;
  constructor(
    public readonly field: string,
    message: string,
    options?: { cause?: unknown },
  ) {
    super(message, options);
  }
}

export class ConflictError extends DomainError {
  readonly tag = 'ConflictError' as const;
}

export class NotFoundError extends DomainError {
  readonly tag = 'NotFoundError' as const;
  constructor(
    public readonly what: string,
    options?: { cause?: unknown },
  ) {
    super(`Not found: ${what}`, options);
  }
}

export class InvariantError extends DomainError {
  readonly tag = 'InvariantError' as const;
}

export function invariant(condition: unknown, message: string): asserts condition {
  if (!condition) throw new InvariantError(message);
}

export type Result<T, E> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: E };

export const ok = <T>(value: T): Result<T, never> => ({ ok: true, value });
export const err = <E>(error: E): Result<never, E> => ({ ok: false, error });
